import { inject, Injectable } from '@angular/core';
import { AccessControl, NativeBiometric } from '@capgo/capacitor-native-biometric';
import { Credenciales } from '@shared/models/login-response.model';
import { TranslateService } from '@shared/translate/translate.service';

@Injectable({
  providedIn: 'root',
})
export class BiometricService {
  private _translate = inject(TranslateService);
  private readonly _server = 'SmartIOT';

  async isBiometricActive(): Promise<boolean> {
    try {
      const credentials = await this.getCredentials();
      return !!credentials;
    } catch (error) {
      console.error('❌ Error al verificar si la biometría está activa:', error);
      return false;
    }
  }

  async biometricVerification(): Promise<void> {
    console.log('➡️ Iniciando verificación biométrica');

    try {
      const availability = await NativeBiometric.isAvailable();
      console.log('🔹 Biometrics disponibles:', availability);

      if (!availability?.isAvailable) throw new Error('Biometric authentication not available');

      await NativeBiometric.verifyIdentity({
        title: this._translate.instant('xLectorBiometriaIngertec'),
        subtitle: this._translate.instant('xIngreseSmartIOT'),
        description: this._translate.instant('xVerifiqueSuIdentidad'),
        maxAttempts: 3,
        useFallback: true,
      });

      console.log('✅ Autenticación biométrica exitosa');
    } catch (error) {
      console.error('❌ Fallo en autenticación biométrica:', error);
      throw error;
    }
  }

  async getSecureCredentials(): Promise<Credenciales | null> {
    try {
      const credentials = await NativeBiometric.getSecureCredentials({
        server: this._server,
        title: this._translate.instant('xLectorBiometriaIngertec'),
        subtitle: this._translate.instant('xIngreseSmartIOT'),
        description: this._translate.instant('xVerifiqueSuIdentidad'),
      });

      if (!credentials) return null;
      return {
        email: credentials.username,
        password: credentials.password,
      };
    } catch (error) {
      console.warn('⚠️ No se pudieron obtener credenciales protegidas:', error);
      return null;
    }
  }

  async getCredentials(): Promise<Credenciales | null> {
    try {
      const credentials = await NativeBiometric.getCredentials({
        server: this._server,
      });

      if (!credentials) return null;
      const credenciales: Credenciales = {
        email: credentials.username,
        password: credentials.password,
      };
      return credenciales;
    } catch (error) {
      console.error('Error obteniendo credenciales:', error);
      return null;
    }
  }

  async setCredentials({ email, password }: Credenciales) {
    return NativeBiometric.setCredentials({
      username: email,
      password,
      server: this._server,
      accessControl: AccessControl.BIOMETRY_ANY,
    });
  }

  async deleteCredentials() {
    await NativeBiometric.deleteCredentials({
      server: this._server,
    });
  }
}
